import React, { useState, useEffect, useContext } from 'react';
import { Container, Row, Col, Form, Card, Button, Modal, Accordion, Badge, Spinner, ToggleButton, ButtonGroup } from 'react-bootstrap';
import axios from 'axios';
import { AppContext } from '../App';
import { format } from 'date-fns';
import '../style.css';

function Activities({ activeTab }) {
    axios.defaults.baseURL = process.env.REACT_APP_BASE_URL;
    const { configList, configData } = useContext(AppContext);
    const [selectedConfigId, setSelectedConfigId] = useState('');
    const [activities, setActivities] = useState([]);
    const [viewMode, setViewMode] = useState('local'); // 'local' or 'cloud'
    const [loading, setLoading] = useState(false);
    const [showTranscriptModal, setShowTranscriptModal] = useState(false);
    const [selectedTranscript, setSelectedTranscript] = useState('');
    const cloud_agent = process.env.REACT_APP_CLOUD_AGENT;
    const local_agent = process.env.REACT_APP_LOCAL_AGENT;
    // Set initial selected config from global state if available
    useEffect(() => {
        if (configData && configData.id) {
            setSelectedConfigId(configData.id);
        } else if (configList && configList.length > 0) {
            setSelectedConfigId(configList[0].id);
        }
    }, [configData, configList]);

    // Fetch activities when selectedConfigId changes or when Activities tab becomes active
    useEffect(() => {
        if (selectedConfigId && activeTab === 'activities') {
            fetchActivities(selectedConfigId);
        }
    }, [selectedConfigId, activeTab]);

    const fetchActivities = async (configId) => {
        setLoading(true);
        try {
            const response = await axios.get(`/get_activities/${configId}`);
            // Sort by started_at descending (newest first)
            const sortedActivities = response?.data?.sort((a, b) => new Date(b.started_at) - new Date(a.started_at));
            setActivities(sortedActivities);
        } catch (error) {
            console.error("Error fetching activities:", error);
            setActivities([]);
        } finally {
            setLoading(false);
        }
    };

    const handleShowTranscript = (transcript) => {
        setSelectedTranscript(transcript);
        setShowTranscriptModal(true);
    };

    const handleCloseTranscript = () => {
        setShowTranscriptModal(false);
        setSelectedTranscript('');
    };

    // Helper to group activities by date
    const groupActivitiesByDate = (activityList) => {
        const groups = {};
        activityList?.forEach(activity => {
            const date = format(new Date(activity.started_at), 'yyyy-MM-dd');
            if (!groups[date]) {
                groups[date] = [];
            }
            groups[date].push(activity);
        });
        return groups;
    };

    const filteredList = activities.filter(activity => {
        const name = activity.agent_name || "";
        // Checking prefix since old logic is with prefix
        const isCloud = name === cloud_agent || name.startsWith('cloud-');
        
        if (viewMode === 'cloud') {
            return isCloud;
        } else {
            return name.startsWith('local-') || name === local_agent || !isCloud;
        }
    });

    const groupedActivities = groupActivitiesByDate(filteredList);

    // Sort dates descending
    const sortedDates = Object.keys(groupedActivities).sort((a, b) => new Date(b) - new Date(a));

    return (
        <Container fluid className="p-3 p-md-5">
            {configData.name === 'neo-allteam-assistant' && configData.agentName === 'neo-allteam-assistant' &&
                <div className="d-flex justify-content-end mb-3">
                    <ButtonGroup>
                        <ToggleButton
                            id="toggle-local"
                            type="radio"
                            className={viewMode === 'local' ? 'btn-custom' : 'btn-custom-outline'}
                            // variant={viewMode === 'local' ? 'primary' : 'outline-primary'}
                            name="radio"
                            value="local"
                            checked={viewMode === 'local'}
                            onChange={(e) => setViewMode(e.currentTarget.value)}>
                            Local Agent
                        </ToggleButton>
                        <ToggleButton
                            id="toggle-cloud"
                            type="radio"
                            className={viewMode === 'cloud' ? 'btn-custom' : 'btn-custom-outline'}
                            // variant={viewMode === 'cloud' ? 'primary' : 'outline-primary'}
                            name="radio"
                            value="cloud"
                            checked={viewMode === 'cloud'}
                            onChange={(e) => setViewMode(e.currentTarget.value)}>
                            Cloud Agent
                        </ToggleButton>
                    </ButtonGroup>
                </div>}

            {loading ? (
                <div className="text-center text-white mt-5">
                    <Spinner animation="border" role="status">
                        <span className="">Loading...</span>
                    </Spinner>
                </div>
            ) : filteredList?.length === 0 ? (
                <div className="text-center text-white mt-5">
                    <h5>No activities found for this configuration.</h5>
                </div>
            ) : (
                <Accordion defaultActiveKey={sortedDates[0]} alwaysOpen>
                    {sortedDates?.map((date) => (
                        <Accordion.Item eventKey={date} key={date} className="mb-3 border-0">
                            <Accordion.Header className="activity-accordion-header mb-2">
                                <div className="d-flex justify-content-between align-items-center w-100">
                                    <span className="text-light fw-bold">
                                        {new Date(date).toLocaleDateString("en-IN", {
                                            weekday: "short",
                                            year: "numeric",
                                            month: "short",
                                            day: "numeric",
                                        })}
                                    </span>

                                    <Badge bg="light" text="dark">
                                        {groupedActivities[date].length} Activities
                                    </Badge>
                                </div>
                            </Accordion.Header>
                            <Accordion.Body className="p-0">
                                {groupedActivities[date]?.map((activity) => (
                                    <Card key={activity.id} className="mb-2 border-0 shadow-sm activity-card">
                                        <Card.Body>
                                            <Row className="align-items-start">
                                                <Col md={2} className="text-muted small">
                                                    <div className="fw-bold text-dark" style={{ fontSize: '1.2rem' }}>
                                                        {format(new Date(activity.started_at), 'h:mm a')}
                                                    </div>
                                                    <div>{activity.country || 'Unknown Location'}</div>
                                                </Col>
                                                <Col md={7}>
                                                    {/* Summary Section */}
                                                    {activity.summary && (
                                                        <div className="mb-2 p-2 rounded" style={{ backgroundColor: '#f8f9fa', borderLeft: '4px solid #50026d' }}>
                                                            <strong>Summary:</strong> <span className="fst-italic">{activity.summary}</span>
                                                        </div>
                                                    )}

                                                    <div className="d-flex gap-3 small text-secondary mt-2">
                                                        <span><i className="bi bi-browser-chrome me-1"></i>{activity.browser}</span>
                                                        <span><i className="bi bi-laptop me-1"></i>{activity.os}</span>
                                                        <span>IP: {activity.ip}</span>
                                                    </div>
                                                </Col>
                                                <Col md={3} className="d-flex flex-column gap-2 align-items-end justify-content-center">
                                                    {activity.recording_url && (
                                                        <div className="w-100 mb-2">
                                                            <audio controls className="w-100" style={{ height: '50px' }}>
                                                                <source src={activity.recording_url} type="audio/mpeg" />
                                                                Your browser does not support the audio element.
                                                            </audio>
                                                        </div>
                                                    )}

                                                    {activity.transcript ? (
                                                        <Button className="btn-custom" size="sm" onClick={() => handleShowTranscript(activity.transcript)}>
                                                            <i className="bi bi-file-text me-1"></i> View Transcript
                                                        </Button>
                                                    ) : <span className="text-muted small">No Transcript</span>}
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>
                                ))}
                            </Accordion.Body>
                        </Accordion.Item>
                    ))}
                </Accordion>
            )}

            {/* Transcript Modal */}
            <Modal show={showTranscriptModal} onHide={handleCloseTranscript} size="lg" centered scrollable>
                <Modal.Header closeButton>
                    <Modal.Title>Conversation Transcript</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ whiteSpace: 'pre-wrap', maxHeight: '60vh', overflowY: 'auto' }}>
                    {selectedTranscript || "No transcript available."}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseTranscript}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
}

export default Activities;